import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Zap, 
  Mail, 
  Send, 
  Calendar, 
  FileText, 
  CheckCircle, 
  Loader2, 
  User, 
  Sparkles, 
  AlertCircle 
} from 'lucide-react';
import { 
  getCollectionDocs, 
  triggerCustomEmail, 
  triggerLeadMagnetEmail, 
  triggerBookingEmail 
} from '../../lib/adminApi';
import { useToast } from '../../hooks/use-toast';

export default function AdminFunctionTriggers() {
  const { toast } = useToast();
  const [activeTriggerTab, setActiveTriggerTab] = useState('lead_magnet'); // 'lead_magnet', 'booking', 'custom'

  // User Picker
  const [usersList, setUsersList] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Form Fields
  const [recipientEmail, setRecipientEmail] = useState('');
  const [firstname, setFirstname] = useState('');
  const [lastname, setLastname] = useState('');

  // Booking specific
  const [bookingDate, setBookingDate] = useState('');
  const [bookingTime, setBookingTime] = useState('');
  const [consultationType, setConsultationType] = useState('1-on-1 Strategy Session');
  const [meetLink, setMeetLink] = useState('https://meet.google.com/piw-qsyu-fqg');

  // Custom Email specific
  const [customSubject, setCustomSubject] = useState('');
  const [customBody, setCustomBody] = useState('');

  const [executing, setExecuting] = useState(false);

  // Load existing users from Firestore bookings / lead_magnets
  useEffect(() => {
    async function loadUsers() {
      setLoadingUsers(true);
      try {
        const [bookings, leads] = await Promise.all([
          getCollectionDocs('bookings', 20),
          getCollectionDocs('lead_magnets', 20)
        ]);

        const combined = [...bookings, ...leads].filter(u => u.email);
        // deduplicate by email
        const unique = [];
        const seen = new Set();
        for (const u of combined) {
          if (!seen.has(u.email)) {
            seen.add(u.email);
            unique.push(u);
          }
        }
        setUsersList(unique);
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingUsers(false);
      }
    }
    loadUsers();
  }, []);

  const handleSelectUser = (userObj) => {
    setSelectedUser(userObj);
    setRecipientEmail(userObj.email || '');
    setFirstname(userObj.firstname || '');
    setLastname(userObj.lastname || '');
    if (userObj.meeting_preferred_date) setBookingDate(userObj.meeting_preferred_date);
    if (userObj.preferred_time) setBookingTime(userObj.preferred_time);
  };

  const handleExecuteTrigger = async (e) => {
    e.preventDefault();
    if (!recipientEmail) {
      toast({ title: 'Validation Error', description: 'Recipient email is required.', variant: 'destructive' });
      return;
    }

    setExecuting(true);
    try {
      if (activeTriggerTab === 'lead_magnet') {
        await triggerLeadMagnetEmail({ email: recipientEmail, firstname, lastname });
        toast({
          title: 'Lead Magnet Email Dispatched 🎉',
          description: `Sent PDF guide email to ${recipientEmail}`,
          variant: 'success'
        });
      } else if (activeTriggerTab === 'booking') {
        await triggerBookingEmail({
          email: recipientEmail,
          firstname,
          meeting_preferred_date: bookingDate || 'your selected date',
          preferred_time: bookingTime || '02:30 PM',
          consultation_type: consultationType,
          meetLink
        });
        toast({
          title: 'Booking Email Dispatched 🎉',
          description: `Sent strategy session confirmation with .ics invite to ${recipientEmail}`,
          variant: 'success'
        });
      } else if (activeTriggerTab === 'custom') {
        if (!customSubject || !customBody) {
          toast({ title: 'Validation Error', description: 'Subject and body content are required.', variant: 'destructive' });
          setExecuting(false);
          return;
        }
        await triggerCustomEmail({
          recipientEmail,
          subject: customSubject,
          bodyHtml: `<p>${customBody.replace(/\n/g, '<br/>')}</p>`
        });
        toast({
          title: 'Custom Email Dispatched 🎉',
          description: `Sent custom branded email to ${recipientEmail}`,
          variant: 'success'
        });
      }
    } catch (err) {
      toast({
        title: 'Execution Failure',
        description: err.message,
        variant: 'destructive'
      });
    } finally {
      setExecuting(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-[#0D1B2A]/90 border border-[#C9A84C]/30 p-6 rounded-2xl shadow-xl flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold font-playfair text-white flex items-center gap-2">
            <Zap className="w-6 h-6 text-[#C9A84C]" />
            Cloud Function Trigger Center
          </h2>
          <p className="text-slate-400 text-xs sm:text-sm">Execute automated email workflows, lead magnet delivery, and calendar invites for selected users.</p>
        </div>

        <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#162236] border border-[#C9A84C]/30 text-xs font-semibold text-[#E8D4A0] uppercase tracking-wider">
          <Sparkles className="w-4 h-4 text-[#C9A84C]" />
          Brand Logo Policy Applied
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-8 items-start">
        {/* Left: User Quick Selection Panel */}
        <Card className="lg:col-span-4 bg-[#0D1B2A]/90 border border-[#C9A84C]/30 rounded-2xl shadow-xl">
          <CardHeader>
            <CardTitle className="text-base font-bold font-playfair text-white flex items-center gap-2">
              <User className="w-5 h-5 text-[#C9A84C]" />
              Select User / Client Record
            </CardTitle>
            <CardDescription className="text-slate-400 text-xs">
              Pick a user from existing bookings or lead magnets to pre-fill details.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 max-h-[500px] overflow-y-auto pr-2">
            {loadingUsers ? (
              <div className="py-6 text-center text-slate-400 text-xs">Loading client list...</div>
            ) : usersList.length === 0 ? (
              <div className="py-6 text-center text-slate-500 text-xs">No client records found. Type recipient manually on the right.</div>
            ) : (
              usersList.map((user, idx) => {
                const isSelected = selectedUser?.email === user.email;
                return (
                  <div
                    key={idx}
                    onClick={() => handleSelectUser(user)}
                    className={`p-3 rounded-xl border cursor-pointer transition-all ${
                      isSelected
                        ? 'bg-[#C9A84C]/15 border-[#C9A84C] text-white'
                        : 'bg-[#162236]/60 border-slate-700/60 text-slate-300 hover:border-[#C9A84C]/40'
                    }`}
                  >
                    <p className="font-semibold text-xs sm:text-sm text-white">{user.firstname || ''} {user.lastname || ''}</p>
                    <p className="text-xs text-[#E8D4A0] font-mono">{user.email}</p>
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>

        {/* Right: Trigger Form */}
        <Card className="lg:col-span-8 bg-[#0D1B2A]/90 border border-[#C9A84C]/30 rounded-2xl shadow-xl">
          <CardHeader>
            {/* Trigger Selection Tabs */}
            <div className="flex border-b border-[#C9A84C]/20 gap-4 pb-4">
              <button
                type="button"
                onClick={() => setActiveTriggerTab('lead_magnet')}
                className={`flex items-center gap-2 pb-2 text-xs sm:text-sm font-semibold transition-all border-b-2 ${
                  activeTriggerTab === 'lead_magnet'
                    ? 'border-[#C9A84C] text-[#E8D4A0]'
                    : 'border-transparent text-slate-400 hover:text-white'
                }`}
              >
                <FileText className="w-4 h-4" />
                1. Re-send Lead Magnet PDF
              </button>

              <button
                type="button"
                onClick={() => setActiveTriggerTab('booking')}
                className={`flex items-center gap-2 pb-2 text-xs sm:text-sm font-semibold transition-all border-b-2 ${
                  activeTriggerTab === 'booking'
                    ? 'border-[#C9A84C] text-[#E8D4A0]'
                    : 'border-transparent text-slate-400 hover:text-white'
                }`}
              >
                <Calendar className="w-4 h-4" />
                2. Re-send Booking Invite (.ics)
              </button>

              <button
                type="button"
                onClick={() => setActiveTriggerTab('custom')}
                className={`flex items-center gap-2 pb-2 text-xs sm:text-sm font-semibold transition-all border-b-2 ${
                  activeTriggerTab === 'custom'
                    ? 'border-[#C9A84C] text-[#E8D4A0]'
                    : 'border-transparent text-slate-400 hover:text-white'
                }`}
              >
                <Mail className="w-4 h-4" />
                3. Send Custom Branded Email
              </button>
            </div>
          </CardHeader>

          <CardContent className="p-6">
            <form onSubmit={handleExecuteTrigger} className="space-y-5">
              {/* Recipient Details */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase font-bold text-slate-300">Recipient Email *</Label>
                  <Input
                    type="email"
                    required
                    value={recipientEmail}
                    onChange={(e) => setRecipientEmail(e.target.value)}
                    placeholder="user@example.com"
                    className="h-11 bg-[#162236] border border-[#C9A84C]/30 text-white rounded-xl text-sm"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase font-bold text-slate-300">First Name</Label>
                  <Input
                    type="text"
                    value={firstname}
                    onChange={(e) => setFirstname(e.target.value)}
                    placeholder="First name"
                    className="h-11 bg-[#162236] border border-[#C9A84C]/30 text-white rounded-xl text-sm"
                  />
                </div>
              </div>

              {/* Tab 2: Booking Specific Fields */}
              {activeTriggerTab === 'booking' && (
                <div className="p-4 bg-[#162236]/60 border border-[#C9A84C]/20 rounded-xl space-y-4">
                  <h4 className="text-xs font-bold text-[#E8D4A0] uppercase tracking-wider">Booking & Meeting Parameters</h4>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-300">Date (YYYY-MM-DD)</Label>
                      <Input
                        type="date"
                        value={bookingDate}
                        onChange={(e) => setBookingDate(e.target.value)}
                        className="h-10 bg-[#0D1B2A] border border-slate-700 text-white text-xs rounded-lg"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-300">Time Slot (CT)</Label>
                      <Input
                        type="text"
                        value={bookingTime}
                        onChange={(e) => setBookingTime(e.target.value)}
                        placeholder="e.g. 02:30 PM"
                        className="h-10 bg-[#0D1B2A] border border-slate-700 text-white text-xs rounded-lg"
                      />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-300">Google Meet Link</Label>
                    <Input
                      type="text"
                      value={meetLink}
                      onChange={(e) => setMeetLink(e.target.value)}
                      className="h-10 bg-[#0D1B2A] border border-slate-700 text-white text-xs rounded-lg font-mono"
                    />
                  </div>
                </div>
              )}

              {/* Tab 3: Custom Email Fields */}
              {activeTriggerTab === 'custom' && (
                <div className="p-4 bg-[#162236]/60 border border-[#C9A84C]/20 rounded-xl space-y-4">
                  <h4 className="text-xs font-bold text-[#E8D4A0] uppercase tracking-wider">Custom Message Composition</h4>
                  <div className="space-y-2">
                    <Label className="text-xs text-slate-300">Subject Line *</Label>
                    <Input
                      type="text"
                      required
                      value={customSubject}
                      onChange={(e) => setCustomSubject(e.target.value)}
                      placeholder="e.g. Update regarding your financial portfolio..."
                      className="h-11 bg-[#0D1B2A] border border-slate-700 text-white text-sm rounded-lg"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs text-slate-300">Email Message Body *</Label>
                    <textarea
                      rows={6}
                      required
                      value={customBody}
                      onChange={(e) => setCustomBody(e.target.value)}
                      placeholder="Type your message content here..."
                      className="w-full bg-[#0D1B2A] border border-slate-700 text-slate-200 text-sm p-3 rounded-lg focus:border-[#C9A84C]"
                    />
                  </div>
                </div>
              )}

              <Button
                type="submit"
                disabled={executing}
                className="w-full h-12 bg-gradient-to-r from-[#C9A84C] to-[#8C6D23] hover:brightness-110 text-[#070E1B] font-bold rounded-xl shadow-lg flex items-center justify-center gap-2 text-base transition-all"
              >
                {executing ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Executing Trigger...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Trigger & Send Email Now
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
